import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Package, Plus, Search, Edit2, RefreshCw, Archive, RotateCcw, Upload, Download, FileSpreadsheet, Loader2, Brain, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { inventoryAPI } from '../../lib/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import api from '../../lib/api';

const EMPTY_FORM = {
  name: '', description: '', sku: '', category: '', item_type: 'service',
  unit_price: '', currency: 'USD', billing_type: 'one_time', tax_rate: '', unit: 'each', is_active: true,
};
const ITEM_TYPES = [{ value: 'product', label: 'Product' }, { value: 'service', label: 'Service' }];
const BILLING_TYPES = [{ value: 'one_time', label: 'One-time' }, { value: 'recurring', label: 'Recurring' }];
const UNITS = ['each', 'hour', 'day', 'month', 'unit', 'license', 'project'];

export default function ProductsCatalog() {
  const currencyCtx = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  // Bulk import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ product: 0, service: 0 });

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      // Server-side pagination — scales to any catalog size (no client cap).
      const params = { limit: PAGE_SIZE, page };
      if (search) params.search = search;
      if (typeFilter !== 'all') params.item_type = typeFilter;
      if (statusFilter === 'active') params.is_active = true;
      else if (statusFilter === 'inactive') params.is_active = false;
      const res = await inventoryAPI.getItems(params);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      // Case-insensitive A→Z within the page so lowercase names aren't dumped last.
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      setItems(data);
      setTotalItems(res.data?.total ?? data.length);
    } catch (err) {
      console.error('Failed to load products:', err);
      toast.error('Failed to load products');
    } finally { setLoading(false); }
  }, [search, typeFilter, statusFilter, page]);

  // Accurate catalog-wide stats (independent of the current page) via cheap count calls.
  const loadStats = useCallback(async () => {
    try {
      const base = {};
      if (statusFilter === 'active') base.is_active = true;
      else if (statusFilter === 'inactive') base.is_active = false;
      const [p, s] = await Promise.all([
        inventoryAPI.getItems({ ...base, item_type: 'product', limit: 1 }),
        inventoryAPI.getItems({ ...base, item_type: 'service', limit: 1 }),
      ]);
      setTypeCounts({ product: p.data?.total ?? 0, service: s.data?.total ?? 0 });
    } catch { /* ignore */ }
  }, [statusFilter]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await inventoryAPI.getItemGroups();
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setCategories(data.map(c => c.name));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadStats(); }, [loadStats]);
  // Reset to page 1 whenever the filters change so results aren't on a stale page.
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter]);

  const openCreate = () => { setEditingItem(null); setForm(EMPTY_FORM); setSheetOpen(true); };
  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '', description: item.description || '', sku: item.sku || '',
      category: item.category || '', item_type: item.item_type || 'service',
      unit_price: item.unit_price ?? '', currency: item.currency || 'USD',
      billing_type: item.billing_type || 'one_time', tax_rate: item.tax_rate ?? '',
      unit: item.unit || 'each', is_active: item.is_active !== false,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, unit_price: parseFloat(form.unit_price) || 0, tax_rate: parseFloat(form.tax_rate) || 0 };
      if (editingItem) {
        await inventoryAPI.updateItem(editingItem.id, payload);
        toast.success(`"${form.name}" updated`);
      } else {
        await inventoryAPI.createItem(payload);
        toast.success(`"${form.name}" created`);
      }
      setSheetOpen(false);
      loadItems(); loadStats();
      loadCategories();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (item) => {
    try { await inventoryAPI.deleteItem(item.id); toast.success(`"${item.name}" deactivated`); loadItems(); loadStats(); }
    catch { toast.error('Deactivate failed'); }
  };
  const handleReactivate = async (item) => {
    try { await inventoryAPI.updateItem(item.id, { is_active: true }); toast.success(`"${item.name}" reactivated`); loadItems(); loadStats(); }
    catch { toast.error('Reactivate failed'); }
  };

  // --- Bulk Import ---
  const handleImport = async () => {
    if (!importFile) { toast.error('Please select a CSV file'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/api/inventory/items/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (res.data.created > 0) {
        toast.success(`${res.data.created} items imported successfully!`);
        loadItems(); loadStats();
        loadCategories();
      }
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} items had issues`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/api/inventory/items/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_catalog_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Catalog exported');
    } catch { toast.error('Export failed'); }
  };

  const fmt = (val) => currencyCtx?.format ? currencyCtx.format(val) : `$${Number(val).toFixed(2)}`;

  const stats = {
    total: totalItems,
    products: typeCounts.product,
    services: typeCounts.service,
  };
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="products-catalog-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Products & Services</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your catalog — the central source for Deals, Quotes & Invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setImportResult(null); setImportFile(null); setImportDialogOpen(true); }} data-testid="import-btn">
            <Upload className="h-4 w-4 mr-1.5" />Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-1.5" />Export CSV
          </Button>
          <Button onClick={openCreate} data-testid="create-product-btn">
            <Plus className="h-4 w-4 mr-2" />New Item
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold" data-testid="stat-total">{stats.total}</p>
          <p className="text-xs text-muted-foreground">Total Items</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-400" data-testid="stat-products">{stats.products}</p>
          <p className="text-xs text-muted-foreground">Products</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400" data-testid="stat-services">{stats.services}</p>
          <p className="text-xs text-muted-foreground">Services</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by name, SKU, description..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px]" data-testid="type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="product">Products</SelectItem>
            <SelectItem value="service">Services</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={loadItems} data-testid="refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="text-left p-3 font-medium">Name</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-left p-3 font-medium">Category</th>
                  <th className="text-right p-3 font-medium">Price</th>
                  <th className="text-left p-3 font-medium">Billing</th>
                  <th className="text-right p-3 font-medium">Tax %</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">Loading...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">
                    {search || typeFilter !== 'all' ? 'No items match your filters' : 'No items yet. Create your first product or service.'}
                  </td></tr>
                ) : items.map(item => (
                  <tr key={item.id} className="hover:bg-muted/20 transition-colors" data-testid={`catalog-row-${item.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{item.name}</div>
                      {item.description && <p className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</p>}
                      {item.sku && <p className="text-[10px] text-muted-foreground/60">SKU: {item.sku}</p>}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={item.item_type === 'product' ? 'text-blue-400 border-blue-500/30' : 'text-emerald-400 border-emerald-500/30'}>
                        {item.item_type === 'product' ? 'Product' : 'Service'}
                      </Badge>
                    </td>
                    <td className="p-3 text-muted-foreground">{item.category || '—'}</td>
                    <td className="p-3 text-right font-mono">{fmt(item.unit_price)}<span className="text-muted-foreground text-xs">/{item.unit || 'each'}</span></td>
                    <td className="p-3">
                      <Badge variant="secondary" className="text-[10px]">{item.billing_type === 'recurring' ? 'Recurring' : 'One-time'}</Badge>
                    </td>
                    <td className="p-3 text-right text-muted-foreground">{item.tax_rate || 0}%</td>
                    <td className="p-3">
                      {item.is_active !== false
                        ? <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[10px]">Active</Badge>
                        : <Badge className="bg-red-500/20 text-red-400 border-0 text-[10px]">Inactive</Badge>}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                        {item.is_active !== false
                          ? <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDeactivate(item)} data-testid={`deactivate-${item.id}`}><Archive className="h-3.5 w-3.5" /></Button>
                          : <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400" onClick={() => handleReactivate(item)} data-testid={`reactivate-${item.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalItems > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-3 px-3 py-3 border-t border-border/50" data-testid="catalog-pagination">
              <p className="text-xs text-muted-foreground" data-testid="catalog-pagination-info">
                Showing {totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalItems)} of {totalItems.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="catalog-prev-page">Previous</Button>
                <span className="text-xs text-muted-foreground tabular-nums">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages || loading}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="catalog-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Off-canvas Sheet: Create / Edit ─── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="product-sheet">
          <SheetHeader>
            <SheetTitle>{editingItem ? 'Edit Item' : 'New Product / Service'}</SheetTitle>
            <SheetDescription>
              {editingItem ? "Update details. Existing snapshots in Deals/Quotes/Invoices won't change." : 'Add a new item to your catalog.'}
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Website Development" data-testid="form-name" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description" rows={2} data-testid="form-description" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.item_type} onValueChange={v => setForm(p => ({ ...p, item_type: v }))}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>SKU</Label>
                <Input value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))} placeholder="Optional" data-testid="form-sku" />
              </div>
              <div>
                <Label>Price</Label>
                <Input type="number" min="0" step="0.01" value={form.unit_price} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} placeholder="0.00" data-testid="form-price" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger data-testid="form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Type</Label>
                <Select value={form.billing_type} onValueChange={v => setForm(p => ({ ...p, billing_type: v }))}>
                  <SelectTrigger data-testid="form-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input type="number" min="0" max="100" step="0.1" value={form.tax_rate} onChange={e => setForm(p => ({ ...p, tax_rate: e.target.value }))} placeholder="0" data-testid="form-tax" />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={v => setForm(p => ({ ...p, unit: v }))}>
                  <SelectTrigger data-testid="form-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Software" list="category-list" data-testid="form-category" />
                <datalist id="category-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="col-span-2 flex items-center gap-3 pt-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} data-testid="form-status" />
                <Label className="cursor-pointer">{form.is_active ? 'Active' : 'Inactive'}</Label>
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
              {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ─── Bulk Import Dialog ─── */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Brain className="h-5 w-5 text-purple-400" />AI-Powered CSV Import</DialogTitle>
            <DialogDescription>
              Upload any CSV file. The AI Brain will auto-detect column mappings and validate data before importing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* File picker */}
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} />
              <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              {importFile ? (
                <p className="text-sm font-medium">{importFile.name} <span className="text-muted-foreground">({(importFile.size / 1024).toFixed(1)} KB)</span></p>
              ) : (
                <p className="text-sm text-muted-foreground">Click to select a CSV file</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">Columns: name, description, type, price, currency, billing_type, tax_rate, sku, category, unit</p>
            </div>

            {/* Import result */}
            {importResult && (
              <div className="rounded-lg border p-4 space-y-2" data-testid="import-result">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {importResult.created > 0 && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                  <span>{importResult.created} items imported</span>
                  {importResult.skipped > 0 && <span className="text-muted-foreground">· {importResult.skipped} skipped</span>}
                </div>
                {importResult.ai_mapping && (
                  <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                    <span className="font-medium text-purple-400">Brain detected:</span> {importResult.ai_mapping}
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="text-xs text-red-400 max-h-24 overflow-y-auto space-y-0.5">
                    {importResult.errors.map((err, i) => <p key={i}>Row {err.row}: {err.message}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Close</Button>
            <Button onClick={handleImport} disabled={importing || !importFile} data-testid="start-import-btn">
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Brain Parsing...</> : <><Brain className="h-4 w-4 mr-2" />Import with AI</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
